import BackButton from 'Components/BackButton'
import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import CustomDateTimePicker from 'Components/CustomDateTimePicker'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import DeleteButton from 'Components/DeleteButton'
import DeleteDialog from 'Components/DeleteDialog'
import FieldErrors from 'Components/FieldErrors'
import MultiSelect from 'Components/MultiSelect/MultiSelect'
import PageTitle from 'Components/PageTitle'
import StyledForm from 'Components/StyledForm'
import StyledLabel from 'Components/StyledLabel'
import RichTextEditor from 'Components/TextEditor'
import { WithLoader } from 'Components/WithLoader'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import { AffiliateNotificationPreviewDialog } from 'Services/SalesManagement/Features/Notification/Components/AffiliateNotificationPreviewDialog'
import { LogDialog } from 'Services/SalesManagement/Features/Notification/Components/LogDialog'
import { IMAGE_UPLOAD_URL } from 'Services/SalesManagement/Features/Notification/constants'
import useNotificationDetail from 'Services/SalesManagement/Features/Notification/Hooks/useNotificationDetail'
import {
  type AffiliateNotificationDetailShape,
  changeAffiliateNotification,
  createAffiliateNotification,
  deleteAffiliateNotification,
  fetchAffiliateNotification,
  fetchAffiliateNotificationOptions,
  NotificationType
} from 'Services/SalesManagement/Features/Notification/notificationSlice'
import useSalesManagementBreadcrumbs from 'Services/SalesManagement/Hooks/useSalesManagementBreadcrumbs'
import useTypedSelector, { useAppDispatch } from 'Store'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import SnackbarUtils from 'Util/Snackbar'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  TextField,
  Typography
} from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect, useId } from 'react'
import { useParams } from 'react-router-dom'
import { t } from 'ttag'

export function AffiliateNotificationDetailPage() {
  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/notifications#${NotificationType.affiliate}`
  const previousLocation = usePreviousLocation(baseUrl)
  const dispatch = useAppDispatch()

  const instanceIdParam = parseInt(useParams<{ id: string }>().id as string, 10)
  const isNewInstance = Number.isNaN(instanceIdParam)

  const initialAffiliateNotificationDetails: AffiliateNotificationDetailShape = {
    id: null,
    active_from: null,
    active_to: null,
    type: '',
    pinned: false,
    title_en: '',
    title_fi: '',
    title_sv: '',
    content_en: '',
    content_fi: '',
    content_sv: '',
    operators: [],
    edit_events: []
  }

  const fieldErrorsDefaultState = {
    active_from: [],
    active_to: [],
    type: [],
    pinned: [],
    title_en: [],
    title_fi: [],
    title_sv: [],
    content_en: [],
    content_fi: [],
    content_sv: [],
    operators: []
  }

  const {
    notificationDetails,
    setNotificationDetails,
    tabValue,
    fieldErrors,
    isSaveDialogOpen,
    setIsSaveDialogOpen,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    isDeleteActionLoading,
    isLogDialogOpen,
    formRef,
    fiEditor,
    setFiEditor,
    enEditor,
    setEnEditor,
    svEditor,
    setSvEditor,
    handleTabChange,
    handleSelectChange,
    handleDateTimePickerChange,
    handleSubmit,
    handleSave,
    handleDelete,
    onSaveClick,
    onDelete,
    viewLog,
    closeLog
  } = useNotificationDetail<AffiliateNotificationDetailShape>({
    initialDetails: initialAffiliateNotificationDetails,
    initialFieldErrors: fieldErrorsDefaultState,
    isNewInstance: isNewInstance,
    instanceIdParam: instanceIdParam,
    previousLocation: baseUrl,
    createAction: createAffiliateNotification,
    updateAction: changeAffiliateNotification,
    deleteAction: deleteAffiliateNotification,
    messages: {
      createSuccess: t`Affiliate notification successfully added.`,
      createError: t`There was an error creating the notification.`,
      updateSuccess: t`Affiliate notification successfully updated.`,
      updateError: t`There was an error editing the notification.`,
      deleteSuccess: t`Affiliate notification successfully deleted.`,
      deleteError: t`There was an error deleting the notification.`
    }
  })

  const pageTitle = isNewInstance
    ? t`Create new affiliate notification`
    : t`Edit affiliate notification`

  const { affiliateNotificationOptions, loading, submitLoading } = useTypedSelector(
    state => state.notification
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: initialAffiliateNotificationDetails changes on every re-render and should not be used as a hook dependency.
  useEffect(() => {
    dispatch(fetchAffiliateNotificationOptions())
    if (isNewInstance) {
      setNotificationDetails(initialAffiliateNotificationDetails)
    } else {
      const params = {
        id: instanceIdParam
      }
      dispatch(fetchAffiliateNotification(params))
        .then(unwrapResult)
        .then(res => {
          setNotificationDetails(res)
        })
        .catch(() => {
          SnackbarUtils.error(t`There was an error fetching the notification.`)
        })
    }
  }, [dispatch, isNewInstance, instanceIdParam, setNotificationDetails])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const breadcrumbs = useSalesManagementBreadcrumbs([
    {
      title: t`Notifications`,
      link: baseUrl
    },
    {
      title: pageTitle
    }
  ])

  const handleTitleOnBlur = (text: string, field: string) => {
    setNotificationDetails(prevState => ({ ...prevState, [field]: text }))
  }

  const handleMultiSelectChange = (
    selected: string[],
    name: keyof AffiliateNotificationDetailShape
  ) => {
    setNotificationDetails(prevState => ({ ...prevState, [name]: selected }))
  }

  const isLoading =
    loading === 'pending' ||
    (!isNewInstance &&
      (!notificationDetails.id || affiliateNotificationOptions.operators.length === 0))

  const handlePinnedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNotificationDetails(prevState => ({ ...prevState, pinned: event.target.checked }))
  }

  const isSubmitLoading = submitLoading === 'pending'

  const user = useTypedSelector(state => state.user.user)

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={2} mb={4}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Breadcrumbs items={breadcrumbs} />
          <BackButton label={t`Back`} link={previousLocation} />
          <Box
            sx={{
              display: 'flex',
              maxWidth: 800,
              justifyContent: 'space-between',
              alignItems: 'start'
            }}
          >
            <PageTitle>{pageTitle}</PageTitle>
            {!isNewInstance && (
              <>
                <DeleteButton onClick={onDelete}>{t`Delete notification`}</DeleteButton>
                <DeleteDialog
                  open={isDeleteDialogOpen}
                  handleClose={() => setIsDeleteDialogOpen(false)}
                  handleSubmit={handleDelete}
                  loading={isDeleteActionLoading}
                  title={t`Delete notification`}
                >
                  <Typography component="span" color="text.primary">
                    {t`Are you sure you want to delete the notification?`}
                  </Typography>
                </DeleteDialog>
              </>
            )}
          </Box>
          {!isNewInstance && isSuperuser(user) && (
            <>
              <CustomButton size="small" onClick={viewLog}>
                {t`View log`}
              </CustomButton>
              <LogDialog
                open={isLogDialogOpen}
                handleClose={closeLog}
                log={notificationDetails.edit_events}
              />
            </>
          )}
        </Grid>
      </Grid>
      <WithLoader loading={isLoading}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <StyledForm onSubmit={handleSave} formRef={formRef} isDisabled={isSubmitLoading}>
              <Box sx={{ minWidth: 200, maxWidth: 400 }}>
                <MultiSelect
                  handleChange={selected => handleMultiSelectChange(selected, 'operators')}
                  label={t`Operator`}
                  required={!isSuperuser(user)}
                  options={affiliateNotificationOptions.operators}
                  initialValues={
                    isNewInstance
                      ? []
                      : notificationDetails.operators.map(operator => [operator, operator])
                  }
                />
                <FieldErrors error={fieldErrors.operators} />
              </Box>
              <Box>
                <FormControl sx={{ minWidth: 200 }} required>
                  <InputLabel>{t`Type`}</InputLabel>
                  <Select
                    value={notificationDetails.type}
                    label={t`Type`}
                    autoWidth
                    onChange={e => handleSelectChange(e, 'type')}
                    error={fieldErrors.type?.length > 0}
                  >
                    {affiliateNotificationOptions.type.map(option => (
                      <MenuItem key={option[0]} value={option[0]}>
                        {option[1]}
                      </MenuItem>
                    ))}
                  </Select>
                  <FieldErrors error={fieldErrors.type} />
                </FormControl>
              </Box>
              <Box>
                <FormControl required>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={notificationDetails.pinned ?? false}
                        onChange={handlePinnedChange}
                      />
                    }
                    label={t`Pin notification`}
                  />
                  <FormHelperText>{t`Pinned notifications are shown at the top for mobile users`}</FormHelperText>
                  <FieldErrors error={fieldErrors.pinned} />
                </FormControl>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <CustomDateTimePicker
                    name="active_from"
                    value={
                      notificationDetails.active_from
                        ? new Date(notificationDetails.active_from)
                        : null
                    }
                    dateToCompare={
                      notificationDetails.active_to ? new Date(notificationDetails.active_to) : null
                    }
                    dateToCompareFieldName="active_to"
                    isStartDate={true}
                    onChange={handleDateTimePickerChange}
                    label={t`Active from`}
                    isError={fieldErrors.active_from?.length > 0}
                    helperText={
                      fieldErrors.active_from?.length > 0 ? fieldErrors.active_from[0].message : ''
                    }
                  />
                  <Box>{` - `}</Box>
                  <CustomDateTimePicker
                    name="active_to"
                    value={
                      notificationDetails.active_to ? new Date(notificationDetails.active_to) : null
                    }
                    dateToCompare={
                      notificationDetails.active_from
                        ? new Date(notificationDetails.active_from)
                        : null
                    }
                    dateToCompareFieldName="active_from"
                    isStartDate={false}
                    onChange={handleDateTimePickerChange}
                    label={t`Active to`}
                    isError={fieldErrors.active_to?.length > 0}
                    helperText={
                      fieldErrors.active_to?.length > 0 ? fieldErrors.active_to[0].message : ''
                    }
                  />
                </Box>
              </Box>
              <Box>
                <StyledLabel>{t`Content`}</StyledLabel>
                <TabContext value={tabValue}>
                  <Box sx={{ backgroundColor: 'background.paper' }}>
                    <Paper>
                      <TabList onChange={handleTabChange} aria-label={t`Notification tab list`}>
                        <Tab label="FI" value="fi" />
                        <Tab label="SV" value="sv" />
                        <Tab label="EN" value="en" />
                      </TabList>
                    </Paper>
                    <TabPanel sx={{ px: 0, pt: 0 }} value="fi">
                      <Box sx={{ px: 2, pt: 4, pb: 1 }}>
                        <TextField
                          fullWidth
                          sx={{ backgroundColor: 'background.default' }}
                          id={useId()}
                          defaultValue={notificationDetails.title_fi}
                          onBlur={event => handleTitleOnBlur(event.target.value, 'title_fi')}
                          label={`${t`Title`} (fi)`}
                          variant="outlined"
                        />
                        <FieldErrors error={fieldErrors.title_fi} />
                      </Box>
                      <Box sx={{ p: 2 }}>
                        <RichTextEditor
                          value={notificationDetails.content_fi ?? ''}
                          setEditor={setFiEditor}
                          placeholder={isNewInstance ? t`Start typing here...` : t`No content`}
                          editor={fiEditor}
                          imageUploadUrl={IMAGE_UPLOAD_URL}
                        />
                        <FieldErrors error={fieldErrors.content_fi} />
                      </Box>
                    </TabPanel>
                    <TabPanel sx={{ px: 0, pt: 0 }} value="sv">
                      <Box sx={{ px: 2, pt: 4, pb: 1 }}>
                        <TextField
                          fullWidth
                          sx={{ backgroundColor: 'background.default' }}
                          id={useId()}
                          defaultValue={notificationDetails.title_sv}
                          onBlur={event => handleTitleOnBlur(event.target.value, 'title_sv')}
                          label={`${t`Title`} (sv)`}
                          variant="outlined"
                        />
                        <FieldErrors error={fieldErrors.title_sv} />
                      </Box>
                      <Box sx={{ p: 2 }}>
                        <RichTextEditor
                          value={notificationDetails.content_sv ?? ''}
                          placeholder={isNewInstance ? t`Start typing here...` : t`No content`}
                          setEditor={setSvEditor}
                          editor={svEditor}
                          imageUploadUrl={IMAGE_UPLOAD_URL}
                        />
                        <FieldErrors error={fieldErrors.content_sv} />
                      </Box>
                    </TabPanel>
                    <TabPanel sx={{ px: 0, pt: 0 }} value="en">
                      <Box sx={{ px: 2, pt: 4, pb: 1 }}>
                        <TextField
                          fullWidth
                          sx={{ backgroundColor: 'background.default' }}
                          id={useId()}
                          defaultValue={notificationDetails.title_en}
                          onBlur={event => handleTitleOnBlur(event.target.value, 'title_en')}
                          label={`${t`Title`} (en)`}
                          variant="outlined"
                        />
                        <FieldErrors error={fieldErrors.title_en} />
                      </Box>
                      <Box sx={{ p: 2 }}>
                        <RichTextEditor
                          value={notificationDetails.content_en ?? ''}
                          placeholder={isNewInstance ? t`Start typing here...` : t`No content`}
                          setEditor={setEnEditor}
                          editor={enEditor}
                          imageUploadUrl={IMAGE_UPLOAD_URL}
                        />
                        <FieldErrors error={fieldErrors.content_en} />
                      </Box>
                    </TabPanel>
                  </Box>
                </TabContext>
              </Box>
              <AffiliateNotificationPreviewDialog
                open={isSaveDialogOpen}
                handleClose={() => setIsSaveDialogOpen(false)}
                handleSubmit={handleSubmit}
                loading={submitLoading === 'pending'}
                isNewInstance={isNewInstance}
                notificationDetails={notificationDetails}
              />
              <CustomLoadingButton
                isLoading={isSubmitLoading}
                onClick={onSaveClick}
                size="large"
                iconPosition="end"
              >
                {t`Save notification`}
              </CustomLoadingButton>
            </StyledForm>
          </Grid>
        </Grid>
      </WithLoader>
    </Box>
  )
}
